import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { withAuth } from '../../hoc/Auth';
import { Column, Container, Wrapper } from '../../hoc/Layout';
import DateRange from '../../Components/Utils/DateRange';
import { MultipleSelect } from '../../Components/Utils/FieldsHelper';
import Button from '../../Components/Utils/UI/Button';
import EmptyCard from '../../hoc/EmptyCard';
import classes from './styles.module.css';
import Api from '../../Api';
import { Parser } from 'json2csv';
import * as XLSX from 'xlsx';

const ReportConstructor = withAuth(function ({ token }) {
    const [filterDate, setFilterDate] = useState([]);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [format, setFormat] = useState('csv');

    // Комиссия провайдера
    const [providerCost, setProviderCost] = useState('0.3');
    const [providerCostFixedMinimum, setProviderCostFixedMinimum] = useState('30');

    // Тип отчёта
    const [reportType, setReportType] = useState('transaction'); // transaction | merchant

    useEffect(() => {
        setIsLoading(true);
        new Api({ token })
            .getProjectsList({ page: 1, itemsPerPage: 10000 })
            .then((data) => {
                const items = data.items || data.data || [];
                const list = items.map((p) => ({
                    uuid: p.uuid,
                    displayName: p.name,
                }));
                setProjects(list);
            })
            .catch((e) => {
                Sentry.captureException(e);
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    }, []);

    function formatMoney(n) {
        if (typeof n !== 'number') n = Number(n || 0);
        return n.toFixed(2).replace('.', ',');
    }

    function formatPercent(p) {
        if (typeof p !== 'number') p = Number(p || 0);
        return String(p).replace('.', ',');
    }

    function makeCSV(data, name) {
        if (!Array.isArray(data) || data.length === 0) {
            alert('Данных для отчёта нет');
            return;
        }

        const universalBOM = '\uFEFF';
        const fields =
            reportType === 'transaction'
                ? [
                      'Мерчант',
                      'Терминал',
                      'id транзакции',
                      'Сумма транзакции',
                      'Комиссия',
                      'Прибыль (%)',
                      'Прибыль(рубли)',
                  ]
                : [
                      'Мерчант',
                      'Терминал',
                      'Кол-во транзакций',
                      'Сумма транзакций',
                      'Сумма комиссии',
                      'Прибыль (%)',
                      'Прибыль(рубли)',
                  ];

        const parser = new Parser({ delimiter: ';', fields });
        const csv = parser.parse(data);

        const a = document.createElement('a');
        a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(universalBOM + csv);
        a.download = (name || 'report') + '.csv';
        a.click();
    }

    function makeExcel(data, name, fmt) {
        if (!Array.isArray(data) || data.length === 0) {
            alert('Данных для отчёта нет');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

        const wbout = XLSX.write(workbook, { bookType: fmt, type: 'array' });
        const blob = new Blob([wbout], {
            type:
                fmt === 'xlsx'
                    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    : 'application/vnd.ms-excel',
        });

        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = (name || 'report') + '.' + fmt;
        a.click();
        URL.revokeObjectURL(a.href);
    }

    function onCreateReport() {
        if (!filterDate || (Array.isArray(filterDate) && filterDate.length < 2)) {
            alert('Выберите период');
            return;
        }
        if (!selectedProject || selectedProject.length === 0) {
            alert('Выберите проекты');
            return;
        }

        const from = filterDate.from || filterDate.startDate || filterDate[0];
        const to = filterDate.to || filterDate.endDate || filterDate[1];
        const projectUuids = selectedProject;

        const cost = parseFloat(providerCost);
        const minCost = parseFloat(providerCostFixedMinimum);

        if (isNaN(cost) || isNaN(minCost) || cost < 0 || minCost < 0) {
            alert('Введите корректные значения комиссии провайдера');
            return;
        }

        const filter = {
            from,
            to,
            projectUuids,
            providerCost: cost,
            providerCostFixedMinimum: minCost,
        };

        setIsLoading(true);

        if (reportType === 'transaction') {
            new Api({ token })
                .getProviderSummary(filter)
                .then((data) => {
                    if (!Array.isArray(data)) {
                        console.error('Invalid response format:', data);
                        alert('Ошибка при формировании отчёта');
                        return;
                    }

                    const rows = data.map((item) => ({
                        Мерчант: item.merchant || 'Не указан',
                        Терминал: item.terminal || 'Не указан',
                        'id транзакции': item.tx_id,
                        'Сумма транзакции': formatMoney(item.amount),
                        Комиссия: formatMoney(item.fee),
                        'Прибыль (%)': formatPercent(item.profit_percent),
                        'Прибыль(рубли)': formatMoney(item.profit),
                    }));

                    const fileName = 'provider_report_transaction';

                    if (format === 'csv') {
                        makeCSV(rows, fileName);
                    } else {
                        makeExcel(rows, fileName, format);
                    }
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    console.error('Ошибка при получении отчёта:', error);
                    alert('Ошибка при формировании отчёта');
                })
                .finally(() => setIsLoading(false));
        } else {
            new Api({ token })
                .getProviderSummaryMerchant(filter)
                .then((data) => {
                    if (!Array.isArray(data)) {
                        console.error('Invalid response format:', data);
                        alert('Ошибка при формировании отчёта');
                        return;
                    }

                    const rows = data.map((item) => ({
                        Мерчант: item.merchant || 'Не указан',
                        Терминал: item.terminal || 'Не указан',
                        'Кол-во транзакций': item.tx_count,
                        'Сумма транзакций': formatMoney(item.amount),
                        'Сумма комиссии': formatMoney(item.fee),
                        'Прибыль (%)': formatPercent(item.profit_percent),
                        'Прибыль(рубли)': formatMoney(item.profit),
                    }));

                    const fileName = 'provider_report_merchant';

                    if (format === 'csv') {
                        makeCSV(rows, fileName);
                    } else {
                        makeExcel(rows, fileName, format);
                    }
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    console.error('Ошибка при получении отчёта:', error);
                    alert('Ошибка при формировании отчёта');
                })
                .finally(() => setIsLoading(false));
        }
    }

    return (
        <Wrapper>
            <Container>
                <Column width="1150">
                    <EmptyCard title={'ReportConstructor.label.reportConstructor'}>
                        {/* === Период === */}
                        <div className={classes.Block}>
                            <div className={classes.Col_10per}>
                                <Button
                                    label={'ReportConstructor.label.period'}
                                    isDisabled={true}
                                    type="text"
                                    style={{ fontWeight: 'bold', cursor: 'default' }}
                                />
                            </div>
                            <div className={classes.Col_30per}>
                                <DateRange onSelectDate={(range) => setFilterDate(range)} defaultPeriod={'month'} />
                            </div>
                        </div>

                        <div className={classes.Divider} />

                        {/* === Мерчант === */}
                        <div className={classes.Block}>
                            <div className={classes.Col_10per}>
                                <Button
                                    label={'ReportConstructor.label.merchant'}
                                    isDisabled={true}
                                    type="text"
                                    style={{ fontWeight: 'bold', cursor: 'default' }}
                                />
                            </div>
                            <div className={classes.Col_30per}>
                                <MultipleSelect
                                    label={'ReportConstructor.label.selectMerchant'}
                                    placeholder={'ReportConstructor.label.selectMerchant'}
                                    values={projects}
                                    selectedValues={selectedProject}
                                    isLoading={isLoading}
                                    onChange={setSelectedProject}
                                    excludeSearchColumn={[]}
                                />
                            </div>
                        </div>

                        <div className={classes.Divider} />

                        {/* === Тип отчёта === */}
                        <div className={classes.Block}>
                            <div className={classes.Col_10per}>
                                <Button
                                    label={'ReportConstructor.label.reportType'}
                                    isDisabled={true}
                                    type="text"
                                    style={{ fontWeight: 'bold', cursor: 'default' }}
                                />
                            </div>
                            <div className={classes.Col_30per}>
                                <select
                                    value={reportType}
                                    onChange={(e) => setReportType(e.target.value)}
                                    className={classes.Select}
                                    disabled={isLoading}
                                >
                                    <option value="transaction">По транзакциям</option>
                                    <option value="merchant">По мерчантам</option>
                                </select>
                            </div>
                        </div>

                        <div className={classes.Divider} />

                        {/* === Комиссия провайдера === */}
                        <div className={classes.Block}>
                            <div className={classes.Col_10per}>
                                <Button
                                    label="ReportConstructor.label.providerFee"
                                    isDisabled={true}
                                    type="text"
                                    style={{ fontWeight: 'bold', cursor: 'default', whiteSpace: 'nowrap' }}
                                />
                            </div>
                            <div className={classes.Col_30per}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={providerCost}
                                        onChange={(e) => setProviderCost(e.target.value)}
                                        placeholder="0.3"
                                        style={{
                                            width: '80px',
                                            padding: '6px 8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                        }}
                                        disabled={isLoading}
                                    />
                                    <span>% от суммы</span>
                                </div>
                            </div>
                        </div>

                        <div className={classes.Block}>
                            <div className={classes.Col_10per}>
                                <Button
                                    label="ReportConstructor.label.minFee"
                                    isDisabled={true}
                                    type="text"
                                    style={{ fontWeight: 'bold', cursor: 'default', whiteSpace: 'nowrap' }}
                                />
                            </div>
                            <div className={classes.Col_30per}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={providerCostFixedMinimum}
                                        onChange={(e) => setProviderCostFixedMinimum(e.target.value)}
                                        placeholder="30"
                                        style={{
                                            width: '80px',
                                            padding: '6px 8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                        }}
                                        disabled={isLoading}
                                    />
                                    <span>рублей</span>
                                </div>
                            </div>
                        </div>

                        <div className={classes.Divider} />

                        {/* === Формат === */}
                        <div className={classes.Block}>
                            <div className={classes.Col_10per}>
                                <Button
                                    label={'ReportConstructor.label.format'}
                                    isDisabled={true}
                                    type="text"
                                    style={{ fontWeight: 'bold', cursor: 'default' }}
                                />
                            </div>
                            <div className={classes.Col_30per}>
                                <select
                                    value={format}
                                    onChange={(e) => setFormat(e.target.value)}
                                    className={classes.Select}
                                    disabled={isLoading}
                                >
                                    <option value="csv">CSV</option>
                                    <option value="xls">XLS</option>
                                    <option value="xlsx">XLSX</option>
                                </select>
                            </div>
                        </div>

                        <div className={classes.Divider} />

                        <div className={classes.Footer}>
                            <Button
                                onClick={onCreateReport}
                                label="ReportConstructor.btn.createReport"
                                icon="file-excel"
                                iconSize={'17px'}
                                isLoading={isLoading}
                            />
                        </div>
                    </EmptyCard>
                </Column>
            </Container>
        </Wrapper>
    );
});

export default ReportConstructor;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/ReportConstructor/index.js